// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

Sentry.init({
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENVIRONMENT,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],

  // Capture 100% of transactions for tracing.
  tracesSampleRate: 1.0,

  // Controls which URLs trace propagation should be enabled
  tracePropagationTargets: [
    'localhost',
    /^https:\/\/dev\.api\.finchlegal\.com/,
    /^https:\/\/staging\.api\.finchlegal\.com/,
    /^https:\/\/api\.finchlegal\.com/,
  ],

  // Fully disable browser replays.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  normalizeDepth: 6,
});

/**
 * RTK Query error shape from .unwrap() rejections
 */
interface RtkQueryError {
  status?: number | string;
  error?: string;
  data?: unknown;
}

/**
 * Options for capturing errors with additional context
 */
interface CaptureErrorOptions {
  /** Tags for filtering/searching in Sentry (e.g., { feature: 'login' }) */
  tags?: Record<string, string>;
  /** Extra data to attach to the error */
  extra?: Record<string, unknown>;
}

/**
 * Properly captures any error type to Sentry with correct Error objects.
 *
 * Handles:
 * - RTK Query errors (objects with status/error/data)
 * - Plain strings
 * - Native Error objects
 * - Unknown error types
 *
 * @example
 * // In a catch block after .unwrap()
 * try {
 *   await someApiCall().unwrap();
 * } catch (e) {
 *   captureError(e, { tags: { feature: 'login' } });
 * }
 */
export const captureError = (
  error: unknown,
  options: CaptureErrorOptions = {},
): void => {
  const { tags = {}, extra = {} } = options;

  // Handle RTK Query errors (most common case)
  if (isRtkQueryError(error)) {
    const message = error.error || `API Error: ${error.status}`;
    Sentry.captureException(new Error(message), {
      tags: {
        http_status: String(error.status),
        ...tags,
      },
      extra: {
        response_data: error.data,
        ...extra,
      },
    });
    return;
  }

  // Handle plain strings
  if (typeof error === 'string') {
    Sentry.captureException(new Error(error), { tags, extra });
    return;
  }

  // Handle native Error objects (pass through)
  if (error instanceof Error) {
    Sentry.captureException(error, { tags, extra });
    return;
  }

  // Handle unknown objects - serialize them
  Sentry.captureException(
    new Error(`Unknown error: ${JSON.stringify(error)}`),
    {
      tags,
      extra: { originalError: error, ...extra },
    },
  );
};

/**
 * Type guard to check if an error is an RTK Query error shape
 */
const isRtkQueryError = (error: unknown): error is RtkQueryError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (typeof (error as RtkQueryError).status === 'number' ||
      typeof (error as RtkQueryError).status === 'string')
  );
};
